import { useRef } from "react";

import { BILLING, formatMoney, formatNumber, formatPercent } from "./licensingTheme";
import { useBarsRise } from "./licensingMotion";
import { ChartTooltip, useChartTooltip } from "./ChartTooltip";

const SEGMENTS = [
  {
    key: "yearly",
    label: BILLING.paid_yearly.label,
    color: BILLING.paid_yearly.color,
    filters: { billingState: "paid_yearly" },
  },
  {
    key: "monthly",
    label: BILLING.paid_monthly.label,
    color: BILLING.paid_monthly.color,
    filters: { billingState: "paid_monthly" },
  },
  {
    key: "cancelling",
    label: BILLING.cancelling.label,
    color: BILLING.cancelling.color,
    filters: { billingState: "cancelling" },
  },
  {
    key: "grace",
    label: BILLING.grace.label,
    color: BILLING.grace.color,
    filters: { billingState: "grace" },
  },
  {
    key: "trialsLive",
    label: "On trial",
    color: "#7fb3c8",
    filters: { trialState: "trial_live" },
  },
  {
    key: "paidNoRecord",
    label: BILLING.paid_no_record.label,
    color: BILLING.paid_no_record.color,
    filters: { billingState: "paid_no_record" },
  },
  {
    key: "lapsed",
    label: BILLING.lapsed.label,
    color: BILLING.lapsed.color,
    filters: { billingState: "lapsed" },
  },
  {
    key: "refunded",
    label: BILLING.refunded.label,
    color: BILLING.refunded.color,
    filters: { billingState: "refunded" },
  },
];

const COLUMNS = [
  { key: "name", label: "School" },
  { key: "teachers", label: "Teachers", align: "right" },
  { key: null, label: "Teachers' plans" },
  { key: "paying", label: "Paying", align: "right" },
  { key: "trialsLive", label: "On trial", align: "right" },
  { key: "collected", label: "Money in", align: "right" },
  { key: "conversion", label: "Trial → paid", align: "right" },
  { key: "demand", label: "Interest", align: "right" },
];

/**
 * Teachers paying for themselves, grouped by where they teach.
 *
 * The sales view of the individual-plan data: five teachers at one school each
 * paying out of pocket is a school-licence conversation nobody is having. The
 * list opens on schools with any individual-plan activity at all; schools
 * where everyone is grandfathered or covered by a licence are one click away,
 * not forty rows of zeros in the way.
 */
export default function SchoolLandscape({ schools, loading, error, query, onQueryChange, onDrill }) {
  const scope = useRef(null);
  const { tip, bind, hide } = useChartTooltip();
  const rows = schools?.rows ?? [];
  const currency = schools?.currency ?? "USD";
  const total = schools?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / (schools?.limit || 25)));
  const schoolFilters = (row) => (row.schoolId ? { schoolId: String(row.schoolId) } : { noSchool: "true" });
  const schoolName = (row) => row.schoolName || "No school linked";

  useBarsRise(scope, [JSON.stringify(rows.map((r) => r.schoolId))], {
    selector: "[data-mix]",
    axis: "x",
    stagger: 0.02,
  });

  const setSort = (key) =>
    onQueryChange({
      sort: key,
      order: query.sort === key && query.order !== "asc" ? "asc" : "desc",
      page: 1,
    });

  return (
    <section className="mcap-lic-panel" aria-labelledby="mcap-lic-schools-title" ref={scope}>
      <header className="mcap-lic-panel-head">
        <div>
          <h3 id="mcap-lic-schools-title" className="mcap-lic-panel-title">
            Schools
          </h3>
          <p className="mcap-lic-panel-sub">
            Teachers who pay for themselves or are on trial, by school. <strong>Interest</strong> = paying + on trial.
          </p>
        </div>
        <div className="mcap-lic-panel-tools">
          <input
            type="search"
            className="mcap-lic-input"
            placeholder="Find a school"
            value={query.search}
            onChange={(event) => onQueryChange({ search: event.target.value, page: 1 })}
            aria-label="Find a school"
          />
          <label className="mcap-lic-toggle">
            <input
              type="checkbox"
              checked={query.activeOnly}
              onChange={(event) => onQueryChange({ activeOnly: event.target.checked, page: 1 })}
            />
            <span>Only schools with activity</span>
          </label>
        </div>
      </header>

      {error && <p className="mcap-lic-error">This panel could not load. {error.message || ""}</p>}

      {!error && (
        <div className="mcap-lic-table-wrap is-flat">
          <table className="mcap-lic-table is-schools">
            <thead>
              <tr>
                {COLUMNS.map((column) => (
                  <th
                    key={column.label}
                    scope="col"
                    className={column.align === "right" ? "is-right" : ""}
                    aria-sort={
                      column.key && query.sort === column.key
                        ? query.order === "asc"
                          ? "ascending"
                          : "descending"
                        : undefined
                    }
                  >
                    {column.key ? (
                      <button type="button" onClick={() => setSort(column.key)}>
                        {column.label}
                        {query.sort === column.key && (
                          <span aria-hidden="true">{query.order === "asc" ? " ↑" : " ↓"}</span>
                        )}
                      </button>
                    ) : (
                      column.label
                    )}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading &&
                Array.from({ length: 5 }, (_, i) => (
                  <tr key={`s${i}`} className="is-skeleton">
                    <td colSpan={COLUMNS.length}>
                      <span />
                    </td>
                  </tr>
                ))}
              {!loading && rows.length === 0 && (
                <tr>
                  <td colSpan={COLUMNS.length} className="mcap-lic-table-loading">
                    {query.search ? "No school matches that search." : "No school has a paying or trial teacher yet."}
                  </td>
                </tr>
              )}
              {!loading &&
                rows.map((row) => {
                  const mixTotal = SEGMENTS.reduce((sum, s) => sum + (row[s.key] ?? 0), 0);
                  return (
                    <tr
                      key={row.schoolId ?? "none"}
                      className="is-clickable"
                      tabIndex={0}
                      onClick={() =>
                        onDrill({
                          type: "cohort",
                          title: schoolName(row),
                          filters: schoolFilters(row),
                        })
                      }
                      onKeyDown={(event) => {
                        if (event.key === "Enter")
                          onDrill({
                            type: "cohort",
                            title: schoolName(row),
                            filters: schoolFilters(row),
                          });
                      }}
                    >
                      <td>
                        <span className="mcap-lic-who">{schoolName(row)}</span>
                        {row.hasSchoolLicense ? (
                          <span className="mcap-lic-chip is-license">Has school licence</span>
                        ) : row.demand > 0 ? (
                          <span className="mcap-lic-chip is-lead">No school licence</span>
                        ) : null}
                      </td>
                      <td className="is-right">{formatNumber(row.teachers)}</td>
                      <td className="mcap-lic-mix-cell">
                        {mixTotal > 0 ? (
                          // The coloured part is as wide as the share of this
                          // school's teachers on an individual plan, so one buyer
                          // among twelve teachers looks like one in twelve.
                          <span
                            className="mcap-lic-mix-track"
                            {...bind({
                              title: schoolName(row),
                              rows: [
                                ...SEGMENTS.filter((s) => row[s.key]).map((s) => ({
                                  label: s.label,
                                  value: formatNumber(row[s.key]),
                                  color: s.color,
                                })),
                                {
                                  label: "Free / no plan",
                                  value: formatNumber(Math.max(0, row.teachers - mixTotal)),
                                  muted: true,
                                },
                              ],
                              footer: "Click a colour to see who",
                            })}
                          >
                            <span
                              className="mcap-lic-mix"
                              data-mix
                              style={{
                                width: `${Math.min(100, (mixTotal / Math.max(row.teachers, 1)) * 100)}%`,
                              }}
                            >
                              {SEGMENTS.map((segment) => {
                                const value = row[segment.key] ?? 0;
                                if (!value) return null;
                                return (
                                  <span
                                    key={segment.key}
                                    role="button"
                                    tabIndex={0}
                                    style={{
                                      flex: value,
                                      background: segment.color,
                                    }}

                                    onClick={(event) => {
                                      event.stopPropagation();
                                      hide();
                                      onDrill({
                                        type: "cohort",
                                        title: `${schoolName(row)} · ${segment.label}`,
                                        filters: {
                                          ...schoolFilters(row),
                                          ...segment.filters,
                                        },
                                      });
                                    }}
                                    aria-label={`${segment.label}: ${value}`}
                                  />
                                );
                              })}
                            </span>
                          </span>
                        ) : (
                          <span className="mcap-lic-muted">None</span>
                        )}
                      </td>
                      <td className="is-right">{formatNumber(row.paying)}</td>
                      <td className="is-right">{formatNumber(row.trialsLive)}</td>
                      <td className="is-right">{formatMoney(row.collectedCents, currency)}</td>
                      <td className="is-right">{formatPercent(row.conversionRate)}</td>
                      <td className="is-right">
                        <span className={`mcap-lic-demand ${row.demand > 0 ? "is-on" : ""}`}>
                          {formatNumber(row.demand)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}

      <footer className="mcap-lic-table-foot">
        <div className="mcap-lic-legend-row">
          {SEGMENTS.map((segment) => (
            <span key={segment.key} className="mcap-lic-legend-item">
              <span className="mcap-lic-legend-dot" style={{ background: segment.color }} aria-hidden="true" />
              {segment.label}
            </span>
          ))}
        </div>
        <div className="mcap-lic-pager">
          {query.activeOnly && schools?.inactiveCount > 0 && (
            <button
              type="button"
              className="mcap-lic-link"
              onClick={() => onQueryChange({ activeOnly: false, page: 1 })}
            >
              Show {formatNumber(schools.inactiveCount)} more school
              {schools.inactiveCount === 1 ? "" : "s"}
            </button>
          )}
          {pages > 1 && (
            <>
              <button
                type="button"
                className="mcap-lic-btn is-ghost"
                disabled={query.page <= 1}
                onClick={() => onQueryChange({ page: query.page - 1 })}
              >
                Previous
              </button>
              <span>
                Page {query.page} of {pages}
              </span>
              <button
                type="button"
                className="mcap-lic-btn is-ghost"
                disabled={query.page >= pages}
                onClick={() => onQueryChange({ page: query.page + 1 })}
              >
                Next
              </button>
            </>
          )}
        </div>
      </footer>

      <ChartTooltip tip={tip} />
    </section>
  );
}
